import { useMemo, useState } from 'react';
import { ChevronUp } from 'lucide-react';
import { DropdownMenu } from '../../../../empreenderUI/ui/DropDown/DropdownMenu';
import { DropDownItem, DropdownMenuContent } from '../../../../empreenderUI/ui/DropDown/DropdownMenuContent';
import { DropdownMenuTrigger } from '../../../../empreenderUI/ui/DropDown/DropdownMenuTrigger';

function parseOrderItems(orderItems) {
  if (!orderItems) return [];
  if (Array.isArray(orderItems)) return orderItems;
  try {
    const parsed = JSON.parse(orderItems);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function getSerials(item) {
  if (!Array.isArray(item?.licenses)) return [];
  return item.licenses.flatMap((license) =>
    (license?.codes || [])
      .map((code) => ({
        licenseName: license?.name,
        serial: code?.serial_number,
      }))
      .filter((entry) => entry.serial),
  );
}

function findUnusedCodesIndex(codesItems, usedCodes, predicate) {
  return codesItems.findIndex((codeItem, index) => {
    if (usedCodes.has(index)) return false;
    return predicate(codeItem);
  });
}

function buildProducts(orderItems, orderItemsCodes) {
  const products = parseOrderItems(orderItems);
  const codesItems = parseOrderItems(orderItemsCodes);
  const usedCodes = new Set();

  const rows = products.map((item) => {
    const name = item?.product?.name;
    const imageUrl = item?.product?.image_url;

    // Prefer exact name match so variants that share an image_url do not collide.
    let matchIndex = findUnusedCodesIndex(
      codesItems,
      usedCodes,
      (codeItem) => Boolean(name) && codeItem?.product?.name === name,
    );

    if (matchIndex < 0) {
      matchIndex = findUnusedCodesIndex(
        codesItems,
        usedCodes,
        (codeItem) => Boolean(imageUrl) && codeItem?.product?.image_url === imageUrl,
      );
    }

    if (matchIndex >= 0) usedCodes.add(matchIndex);

    return {
      product: item?.product,
      serials: matchIndex >= 0 ? getSerials(codesItems[matchIndex]) : getSerials(item),
    };
  });

  codesItems.forEach((codeItem, index) => {
    if (usedCodes.has(index)) return;
    const serials = getSerials(codeItem);
    if (serials.length === 0 && !codeItem?.product) return;
    rows.push({
      product: codeItem?.product,
      serials,
    });
  });

  return rows;
}

export default function OrderProductsGroup({ orderItems, orderItemsCodes, id }) {
  const [isOpen, setIsOpen] = useState(false);
  const products = useMemo(() => buildProducts(orderItems, orderItemsCodes), [orderItems, orderItemsCodes]);

  if (products.length === 0) {
    return <span className='text-sm text-gray-400'>0</span>;
  }

  return (
    <DropdownMenu isOpen={isOpen} setIsOpen={setIsOpen}>
      <DropdownMenuTrigger className='w-auto'>
        <div className='flex items-center cursor-pointer'>
          {products.map((item, index) => (
            <img
              key={`${id}-${index}`}
              alt={item?.product?.name || ''}
              className='w-10 h-10 rounded-full border bg-white object-cover'
              style={{ transform: index > 0 ? `translateX(-${index * 10}px)` : 'translateX(0)' }}
              src={item?.product?.image_url}
            />
          ))}
          <ChevronUp
            className='h-5 w-5 text-gray-900 shrink-0'
            style={{
              transform: `translateX(-${products.length * 5}px) rotate(${isOpen ? '0deg' : '180deg'})`,
            }}
          />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent className='min-w-28' sideOffset={1} align='start'>
        {products.map((item, index) => (
          <DropDownItem key={`${id}-item-${index}`}>
            <div className='flex gap-2 items-start p-2'>
              <img className='w-10 h-10 rounded object-cover shrink-0' alt='' src={item?.product?.image_url} />
              <div className='min-w-0 flex flex-col gap-0.5'>
                <span className='text-sm max-w-[220px] truncate font-medium'>{item?.product?.name}</span>
                {item.serials.map((entry, serialIndex) => (
                  <span
                    key={`${id}-serial-${index}-${serialIndex}`}
                    className='text-xs text-gray-600 font-mono truncate max-w-[220px]'
                  >
                    {entry.licenseName ? `${entry.licenseName}: ` : ''}
                    {entry.serial}
                  </span>
                ))}
              </div>
            </div>
          </DropDownItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
